(ns frontend.react.virtualized.scrollable-grid.common
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [cognitect.transit :as t]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.fields.filter-types :as filters]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.editor-types :as editor-types]
   [common.schema.registry :as registry]
   [common.timer :as timer]
   [common.util :as cu]
   [frontend.db :as app-db]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [frontend.utils :as fu]
   [frontend.view.components.filter :as filter]
   [frontend.view.grid.common.no-results-view :as no-results-view]
   [frontend.view.modals.curtains.column-selector-curtain.view :as column-selector-curtain]
   [frontend.view.modals.curtains.filter-curtain.view :as filter-curtain]
   [frontend.view.modals.events :as modals]
   [potpuri.core :as p :refer [dissoc-in]]
   [reagent.core :as r]
   [ui.dom-globals :as dg]
   [ui.javascript-utils :as jsu]))

;;column related fns:

(declare clear-selection!)

(defn selected-columns
  [{:keys [selected-columns-state default-columns]}]
  (-> @selected-columns-state
      (or default-columns)
      set))

(defn column-selected?
  [{:keys [mandatory?] :as setup-args} column-key]
  (or (contains? mandatory? column-key)
      (contains? (selected-columns setup-args) column-key)))

(defn sorted-columns
  [{:keys [ordered-columns-state column-keys]}]
  (let [ordered-columns @ordered-columns-state
        ordered (set ordered-columns)
        unordered-columns (->> (column-keys)
                               (remove ordered))]
    (concat ordered-columns unordered-columns)))

(defn resolve-column-key-universe
  [setup-args]
  (->> (sorted-columns setup-args)
       vec))

(defn resolve-column-keys
  [{:keys [column-keys] :as setup-args}]
  (->> (sorted-columns setup-args)
       (filter (set (column-keys)))                                                                 ;; show only available columns
       (filter (fn [column-key]
                 (column-selected? setup-args column-key)))
       vec))

;;universal search related fns:

#_(defn universal-match-term?
    [search-term values]
    (let [match-term? (fn [value term]
                        (string/includes? value search-term))]
      (some (fn-> (string/includes? search-term)) values)))

(defn universal-match-term?
  [^String search-term values]
  (let [match-term? (fn [^String value]
                      (.includes value search-term))]
    (some match-term? values)))

(defn search-terms
  [^String search-string]
  (let [terms (-> search-string
                  str
                  string/trim
                  string/lower-case
                  (string/split #"\s+"))]
    (->> terms
         distinct
         (sort-by count >))))

(defn get-display-data
  [tenant-schema tenant visible-keys-and-schemas data]
  (let [displays (->> visible-keys-and-schemas
                      (keep (fn [[k {:field/keys [calculated-field] :as fs}]]
                              (when (or (contains? data k)
                                        (and (vector? k)
                                             (contains? data (first k)))
                                        calculated-field)
                                (let [val (fu/display-data tenant-schema tenant k fs data)]
                                  (when-not (cu/blank-value? val)
                                    [k val])))))
                      (into {}))]
    (assoc
     (reduce-kv assoc
                data
                displays)
     :raw-data
     data)))

;;example speed test to run from cljs repl (update id with an existing entity from your local env)
#_(let [entity (get @session/entities 17592186047520)
        v (:v @speed-vals)]
    ;;note: temporarily added
    ;;  (swap! speed-vals assoc :v visible-keys-and-schemas)
    ;; inside 'recalculate'
    (time
     (do
       (dotimes [i 20000]
         (get-display-data tenant-schema tenant v entity))
       nil)))

(defn js-universal-match?
  [_keys-and-schemas attributes search-terms _entity display-data-entity]
  (let [;; searchable attribute values as a list of strings
        values (->> display-data-entity
                    (keep (fn [[k v]]
                            (when (and (contains? attributes k)
                                       v)
                              (.trim (if (string? v)
                                       v
                                       (.toString v))))))
                    (map (fn [s]
                           (.toLowerCase s))))]
    (every? (fn [term]
              (universal-match-term? term values))
            search-terms)))

#_(let [entity (get @session/entities 17592186047520)
        attributes (:attributes @speed-vals)
        search-terms ["foo" "ba"]
        v (:v @speed-vals)]
    ;;note: temporarily added
    ;;  (swap! speed-vals assoc :v visible-keys-and-schemas)
    ;; inside 'recalculate'
    (time
     (do
       (dotimes [i 2000]
         (js-universal-match? v attributes search-terms entity))
       nil)))

(defn ^js/Array js-name-search-filter
  [name-search-text ^js/Array entities]
  (let [length (jsu/array-length entities)
        result (array)]
    (dotimes [^js/Number index length]
      (let [entity (aget entities index)
            entity-type (:entity/type entity)
            name-lcase (or (when-not (contains? #{:entity.type/account
                                                  :entity.type/person
                                                  :entity.type/fund-commitment
                                                  :entity.type/screening-profile}
                                                entity-type)
                             (:common/lower-case-str entity))
                           (cu/str-trim-lcase (fu/resolve-name entity)))]
        (when (string/includes? name-lcase name-search-text)
          (jsu/array-push result entity))))
    result))

(defn ^js/Array js-universal-search-filter
  [setup-args search-string ^js/Array entities]
  (let [;; search only visible columns where text search applies
        search-terms (search-terms search-string)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        attributes
        (->> (resolve-column-keys setup-args)
             (filter (fn [attribute]
                       (let [field-schema (registry/get-field-schema tenant-schema attribute)
                             _data-type (-> field-schema
                                            :field/data-type)
                             grid-type (-> field-schema
                                           :field/default-visibility
                                           :grid)
                             value-type (-> field-schema
                                            :value-type)]
                         (or (not field-schema)
                             (editor-types/text-like-editor-type? grid-type)
                             (= value-type :entity)))))
             set)
        length (jsu/array-length entities)
        keys-and-schemas (->> setup-args
                              :visible-keys-and-schemas
                              (filter (fn [[k _]]
                                        (contains? attributes k)))
                              doall)
        result (array)]
    (dotimes [^js/Number index length]
      (let [entity (aget entities index)
            display-data-entity (get-display-data tenant-schema tenant keys-and-schemas entity)]
        (when
         (js-universal-match? keys-and-schemas attributes search-terms entity display-data-entity)
          (jsu/array-push result entity))))
    result))

;;filter related fns:

(defn convert-to-int
  [v]
  (js/parseInt (str v) 10))

(defn convert-to-float
  [v]
  (js/parseFloat (str v)))

(defn convert-to-date-str
  [v]
  (str v))

(defn convert-to-boolean
  [v]
  (boolean v))

(defn convert-to-text
  [v]
  (if (string? v)
    v
    (str v)))

(defn convert-to-keyword
  [v]
  (cond
    (keyword? v) v
    (string? v) (keyword v)
    :else (keyword (str v))))

(defn convert-to-ids
  [v]
  v)

(defn convert-select
  [data-type value]
  (case data-type
    :data.type/keyword value
    :data.type/keyword-multi value
    :data.type/text value
    :data.type/datetime value
    :data.type/date value
    :data.type/email-multi value
    :data.type/email value
    :data.type/currency-symbol value
    :data.type/currency-value value
    :data.type/text-url value
    :data.type/text-simple value
    :data.type/long value
    :data.type/percentage value
    :data.type/multiplier value
    :data.type/number value
    :data.type/boolean value
    :data.type/text-multiline value
    :data.type/text-edn value
    :data.type/integer value
    :data.type/text-richtext value
    :data.type/ref-single value
    :data.type/ref-multi value
    :data.type/id value))

(defn coerce-to-comparable-value
  [data-type filter-type operand value]
  (case filter-type
    :editor.type/none nil
    :editor.type/financials nil
    :editor.type/allocation-split nil
    :editor.type/grid (convert-select data-type value)
    :editor.type/text-simple (convert-to-text value)
    :editor.type/select (convert-select data-type value)
    :editor.type/url (convert-to-text value)
    :editor.type/checkbox (convert-to-boolean value)
    :editor.type/radio (convert-to-boolean value)
    :editor.type/date (convert-to-date-str value)
    :editor.type/percentage (some-> value
                                    convert-to-float)
    :editor.type/multiplier (some-> value
                                    convert-to-float)
    ;;TODO should we use filter-type instead of editor-type? and use separate filter-type
    ;;     for currency-fields:
    :editor.type/number (some-> value
                                fu/if-bigdec-then-float
                                convert-to-float)
    :editor.type/integer (some-> value
                                 convert-to-int)
    :editor.type/multi-select (convert-select data-type value)
    :editor.type/wysiwyg (convert-to-text value)
    :editor.type/related-entities (convert-to-ids value)
    :editor.type/toggle (convert-to-boolean value)
    :editor.type/textarea (convert-to-text value)
    :editor.type/email (convert-to-text value)
    :editor.type/datetime (convert-to-date-str value)
    :editor.type/link (convert-to-text value)
    (logging/error "NO Coercion for " data-type " : " filter-type " : " operand " >> " value)))

(defn coerce-to-comparable-filter-value-single
  [data-type filter-type operand value]
  (case filter-type
    :editor.type/none nil
    :editor.type/financials nil
    :editor.type/allocation-split nil
    :editor.type/grid (convert-select data-type value)
    :editor.type/text-simple (convert-to-text value)
    :editor.type/select (convert-select data-type value)
    :editor.type/url (convert-to-text value)
    :editor.type/checkbox (convert-to-boolean value)
    :editor.type/radio (convert-to-boolean value)
    :editor.type/date (convert-to-date-str value)
    :editor.type/percentage (convert-to-float value)
    :editor.type/multiplier (convert-to-float value)
    :editor.type/number (convert-to-float value)
    :editor.type/integer (convert-to-int value)
    :editor.type/multi-select (convert-select data-type value)
    :editor.type/wysiwyg (convert-to-text value)
    :editor.type/related-entities (convert-to-ids value)
    :editor.type/toggle (convert-to-boolean value)
    :editor.type/textarea (convert-to-text value)
    :editor.type/email (convert-to-text value)
    :editor.type/datetime (convert-to-date-str value)
    :editor.type/link (convert-to-text value)
    (logging/error "NO Coercion for filter value " data-type
                   " : " filter-type
                   " : " operand
                   " >> " value)))

(defn coerce-to-comparable-filter-value
  [_data-type _filter-type _operand value]
  value

  #_(if (and (some #{operand} filters/multi-operands)
             (coll? value))
      (->> value
           (map (partial coerce-to-comparable-filter-value-single data-type filter-type operand))
           (remove nil?)
           (into []))
      (coerce-to-comparable-filter-value-single data-type filter-type operand value)))

(defn filter-entity
  [tenant-schema tenant [attribute filter-data] entity]
  (let [{:keys [operand lower-bound-value upper-bound-value]} filter-data
        field-schema (registry/get-field-schema tenant-schema attribute)
        field-data-type (->> field-schema
                             :field/data-type)
        filter-type (->> field-schema
                         :field/default-visibility
                         :filter)
        filter-value-type (filters/editor-by-filter-type filter-type)

        entity-value (fu/get-value tenant tenant-schema entity attribute)
        entity-value
        (coerce-to-comparable-value field-data-type filter-value-type operand entity-value)
        lower-bound-value (coerce-to-comparable-filter-value field-data-type
                                                             filter-value-type
                                                             operand
                                                             lower-bound-value)
        upper-bound-value (coerce-to-comparable-filter-value field-data-type
                                                             filter-value-type
                                                             operand
                                                             upper-bound-value)
        res (filters/filter-value filter-type
                                  operand
                                  entity-value
                                  lower-bound-value
                                  upper-bound-value
                                  (when (session/is-admin?)
                                    (partial logging/warn)))]
    res))

(defn apply-filter
  [tenant-schema tenant entity filter-data]
  (let [[attribute filter-info] filter-data
        custom-filter-fn (get filter/custom-filter-functions attribute)]
    (if (fn? custom-filter-fn)
      (not (custom-filter-fn filter-info entity))
      ;; IF FILTER DOES NOT MATCH - ENTITY SHOULD NOT BE SHOWN!
      (let [filter-result (filter-entity tenant-schema tenant filter-data entity)]
        (not filter-result)))))

(defn apply-filters
  [filters entity]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema]
    (->> filters
         (filter (fn [[_filter-key filter-config :as filter-data]]
                   (if-not (= (:filter/type filter-config) :filter.type/or-combination)
                     (apply-filter tenant-schema tenant entity filter-data)
                     (->> filter-config
                          :filter/child-filters
                          (filter (fn [child-filter]
                                    (let [child-filter?
                                          (apply-filter tenant-schema tenant entity child-filter)]
                                      (not child-filter?))))
                          ;; if any match ->> OR
                          (empty?)))))
         (empty?))))                                                                                ;; SHOW ONLY IF NO RULES THAT WOULD MATCH OUT

(defn filter-grid-entities
  [grid-entities filters]
  (if (empty? filters)
    grid-entities
    (some->> grid-entities
             (filter (partial apply-filters filters)))))

(defn ^js/Array js-filter-grid-entities
  [^js/Array js-grid-entities filters]
  (if (empty? filters)
    js-grid-entities
    (let [length (jsu/array-length js-grid-entities)
          apply-current-filters (partial apply-filters filters)
          result ^js/Array (array)]
      (dotimes [index length]
        (let [element (aget js-grid-entities index)]
          (when (apply-current-filters (aget js-grid-entities index))
            (jsu/array-push result element))))
      result)))

(defn sort-grid-entities
  [entities sort-by-key]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema sort-by-key)
        data-type (-> field-schema
                      :field/data-type)
        sort-type (->> data-type
                       (get data-types/zapflow-data-types)
                       :sort-type)
        all-entities @session/entities]
    (if-not sort-by-key
      entities
      (some->>
       entities
       (sort-by
        (fn [entity]
          (let [value (fu/get-value tenant tenant-schema entity sort-by-key)
                sorted-value
                (if-not (data-types/is-of-type data-type value t/bigdec?)
                  nil
                  (case sort-type
                    :sort.type/native-value (if (string? value)
                                              (fu/trimmed-lower-case (cu/truncate 5 value))
                                              value)
                    :sort.type/currency-value (fu/bigdec->float value)
                    ;:sort.type/display-string - and else
                    (let [coerced-val (fu/display-data tenant-schema
                                                       tenant
                                                       sort-by-key
                                                       field-schema
                                                       entity
                                                       :entities
                                                       all-entities)]
                      (if (string? coerced-val)
                        (fu/trimmed-lower-case coerced-val)
                        coerced-val))))]
            sorted-value)))))))

(defn js-sort-grid-entities
  [entities sort-by-key secondary-sort-by-key secondary-sort-direction]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema sort-by-key)
        secondary-field-schema (registry/get-field-schema tenant-schema secondary-sort-by-key)
        data-type (-> field-schema
                      :field/data-type)
        secondary-data-type (:field/data-type secondary-field-schema)
        sort-type (->> data-type
                       (get data-types/zapflow-data-types)
                       :sort-type)
        secondary-sort-type (->> secondary-data-type
                                 (get data-types/zapflow-data-types)
                                 :sort-type)
        all-entities @session/entities
        sort-value (fn [entity sort-by-key-arg field-schema-arg data-type-arg sort-type-arg]
                     (let [value (fu/get-value tenant tenant-schema entity sort-by-key-arg)
                           sorted-value (if (and data-type-arg
                                                 (not (data-types/is-of-type data-type-arg value t/bigdec?)))
                                          nil
                                          (case sort-type-arg
                                            :sort.type/native-value (if (string? value)
                                                                      (fu/trimmed-lower-case (cu/truncate 5 value))
                                                                      value)
                                            :sort.type/currency-value (fu/bigdec->float value)
                                            ;:sort.type/display-string - and else
                                            (let [coerced-val (fu/display-data tenant-schema
                                                                               tenant
                                                                               sort-by-key-arg
                                                                               field-schema-arg
                                                                               entity
                                                                               :entities
                                                                               all-entities)]
                                              (if (string? coerced-val)
                                                (fu/trimmed-lower-case coerced-val)
                                                coerced-val))))]
                       sorted-value))
        _add-sorts
        (dotimes [index (jsu/array-length entities)]
          (let [entity (aget entities index)
                sorted-value (sort-value entity sort-by-key field-schema data-type sort-type)
                secondary-sorted-value (sort-value entity
                                                   secondary-sort-by-key
                                                   secondary-field-schema
                                                   secondary-data-type
                                                   secondary-sort-type)
                updated-entity (assoc entity
                                      :sorted-value sorted-value
                                      :secondary-sorted-value secondary-sorted-value
                                      :name-sort-value (entity/display-string entity))]
            (aset entities index updated-entity)))]
    (if-not sort-by-key
      entities
      (do
        (jsu/array-sort entities
                        (fn [e1 e2]
                          (let [c (compare (:sorted-value e1) (:sorted-value e2))]
                            (cond
                              (not= c 0)
                              c

                              (not secondary-sort-by-key)
                              (compare (:name-sort-value e1) (:name-sort-value e2))

                              :else
                              (let [s1 (:secondary-sorted-value e1)
                                    s2 (:secondary-sorted-value e2)
                                    c2 (compare s1 s2)]
                                (cond (= c2 0)
                                      (compare (:name-sort-value e1) (:name-sort-value e2))
                                      (= "DESC" secondary-sort-direction)
                                      (- c2)
                                      :else
                                      c2))))))
        entities))))

(defn recalculate
  [{:keys [re-sort? state view-settings-state filters-state custom-filters custom-sort-fn visible-keys-and-schemas-fn kanban?]
    :as   setup-args}]
  (let [timer (timer/create-timer)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        all-timer (timer/create-timer)
        grid-entities (some->> @state
                               :grid-entities)
        _ (logging/debug "t-start>" (timer) " >> " (pr-str @view-settings-state))

        js-grid-entities (into-array grid-entities)
        _ (logging/debug "t-after-into-array>" (timer))

        visible-keys-and-schemas (when visible-keys-and-schemas-fn
                                   (visible-keys-and-schemas-fn))
        sort-by-key (or (:sort-by (when view-settings-state @view-settings-state))
                        :common/created-on)
        secondary-sort-by-key (or (:secondary-sort-by (when view-settings-state
                                                        @view-settings-state))
                                  :common/lower-case-str)
        sort-direction (or (:sort-direction (when view-settings-state
                                              @view-settings-state))
                           "DESC")
        secondary-sort-direction (or (:secondary-sort-direction (when view-settings-state
                                                                  @view-settings-state))
                                     "DESC")
        _ (logging/debug "t-before-filter>" (timer))

        filters (when filters-state @filters-state)
        js-filtered-grid-entities (js-filter-grid-entities js-grid-entities
                                                           (if custom-filters
                                                             (concat custom-filters filters)
                                                             filters))
        _ (logging/debug "t-after-filter>" (timer))

        name-search-text (some->> @state
                                  :search-name
                                  :input
                                  (cu/str-trim-lcase))
        js-name-search-filtered (if-not (string/blank? name-search-text)
                                  (js-name-search-filter name-search-text js-filtered-grid-entities)
                                  js-filtered-grid-entities)
        _ (logging/debug "t-after-name>" (timer))

        universal-search-text (some->> @state
                                       :search
                                       :input)
        js-universal-search-filtered (if-not (string/blank? universal-search-text)
                                       (js-universal-search-filter (assoc setup-args
                                                                          :visible-keys-and-schemas
                                                                          visible-keys-and-schemas)
                                                                   universal-search-text
                                                                   js-name-search-filtered)
                                       js-name-search-filtered)
        _ (logging/debug "t-after-universal>" (timer) " >> " js-universal-search-filtered)

        length (jsu/array-length js-universal-search-filtered)
        js-non-blanks (array)
        js-blanks (array)
        _ (dotimes [index length]
            (let [entity (aget js-universal-search-filtered index)
                  val (fu/get-value tenant tenant-schema entity sort-by-key)
                  empty-value? (or (nil? val)
                                   (and (string? val) (string/blank? val))
                                   (and (coll? val) (empty? val)))]
              (if empty-value?
                (jsu/array-push js-blanks entity)
                (jsu/array-push js-non-blanks entity))))
        _ (logging/debug "t-after-blanks>" (timer))
        previous-sort-orders (:previous-sort-orders @state)
        result
        ^js/Array
        (if (and previous-sort-orders (not re-sort?))
          (-> js-universal-search-filtered
              (jsu/array-sort (fn [e1 e2]
                                (compare (get previous-sort-orders (:db/id e1))
                                         (get previous-sort-orders (:db/id e2))))))
          (let [js-sorted-grid-entities (if custom-sort-fn
                                          (into-array (custom-sort-fn js-non-blanks sort-by-key))
                                          (js-sort-grid-entities js-non-blanks
                                                                 sort-by-key
                                                                 secondary-sort-by-key
                                                                 secondary-sort-direction))
                js-sorted-blanks (if custom-sort-fn
                                   (into-array (custom-sort-fn js-blanks sort-by-key))
                                   (js-sort-grid-entities js-blanks
                                                          sort-by-key
                                                          secondary-sort-by-key
                                                          secondary-sort-direction))]
            (if (= "DESC" sort-direction)
              (-> js-sorted-grid-entities
                  (jsu/array-reverse)
                  (jsu/array-concat js-sorted-blanks))
              (jsu/array-concat js-sorted-grid-entities js-sorted-blanks))))
        _ (logging/debug "t-after-sorts>" (timer))
        render-data result
        _ (logging/debug "t-after-render-data>" (timer))
        render-data (into [] render-data)]
    (when (logging/print-debug?)
      (logging/log (str (all-timer)
                        "ms Recalc grid -> " (routing/view)
                        " " (timer)
                        " << " (pr-str sort-by-key)
                        " : " sort-direction
                        " >> " re-sort?)))
    (swap! state assoc
           :previous-sort-orders
           (->> result
                (map-indexed (fn [idx o]
                               [(:db/id o) idx]))
                (into {})))
    (when kanban?
      (swap! state assoc :sorted-kanban-entities (vec result)))
    (swap! state assoc :sorted-grid-entities render-data)))

(defn reorder-columns!
  [{:keys [ordered-columns-state ls-path] :as setup-args}
   dragged-column after-column]
  (when-not (= dragged-column after-column)
    (let [ordered (resolve-column-keys setup-args)
          items (remove #{dragged-column} ordered)
          pos (or (p/find-index items #{after-column})
                  -1)
          head (take (inc pos) items)
          tail (drop (inc pos) items)
          new-order (vec (concat head [dragged-column] tail))]
      (reset! ordered-columns-state new-order)
      (when ls-path
        (ls/update-in (ls-path) assoc :ordered-columns new-order)))))

(defn select-column!
  [selected-columns-state ls-path column-key]
  (swap! selected-columns-state set/union #{column-key})
  (deref selected-columns-state)
  (when ls-path
    (ls/update-in (ls-path :selected-columns)
                  set/union
                  #{column-key})))

(defn unselect-column!
  [selected-columns-state ls-path column-key]
  (swap! selected-columns-state set/difference #{column-key})
  (deref selected-columns-state)
  (when ls-path
    (ls/update-in (ls-path :selected-columns)
                  set/difference
                  #{column-key})))

(defn open-select-columns-curtain
  [{:keys [selected-columns-state column-keys column-tabs
           resolve-tab mandatory? ls-path]
    :as   setup-args}]
  (let [tenant-schema @session/tenant-schema
        temp-state (r/atom (set @selected-columns-state))]
    (modals/open-modal!
     column-selector-curtain/modal-key
     {:header-text      (loc/tr :curtain-select-columns/header)
      :values-row-width 3
      :label-row-width  9
      :options          {true  [:span.label-sm (loc/tr :column/selected)]
                         false [:span.label-sm (loc/tr :column/not-selected)]}
      :state            temp-state
      :column-tabs      column-tabs
      :resolve-tab      resolve-tab
      :fields-getter    (fn [_]
                          (->>
                           (column-keys)
                           (map
                            (fn [k]
                              (let [label (cond (empty? column-tabs)
                                                (attribute/attribute-label tenant-schema k)
                                                (keyword? k)
                                                (attribute/attribute-label tenant-schema k)
                                                :else
                                                (attribute/attribute-label tenant-schema (last k)))]
                                {:field/name      label
                                 :field/value     (boolean (some #{k} @temp-state))
                                 :field/key       k
                                 :field/disabled? (contains? mandatory? k)})))
                           vec))
      :on-change!       (fn [k v]
                          (if v
                            (select-column! temp-state ls-path k)
                            (unselect-column! temp-state ls-path k)))
      :on-save!         (fn [_]
                          (reset! selected-columns-state @temp-state)
                          (deref selected-columns-state)
                          (recalculate (assoc setup-args :re-sort? true)))})))

(defn add-filter
  [{:keys [filters-state ls-path] :as setup-args} k filter-data]
  (swap! filters-state assoc k filter-data)
  ;;when filters-to-ls?
  (ls/update-in (ls-path :filters)
                assoc
                k
                (->> filter-data
                     (filter (fn [[_ v]]
                               (not (fn? v))))
                     (into {})))
  (clear-selection! setup-args)
  (recalculate setup-args))

(defn remove-filter
  [{:keys [filters-state ls-path] :as setup-args} k]
  (swap! filters-state dissoc k)
  ;;when filters-to-ls?
  (ls/update-in (ls-path :filters) dissoc k)
  (recalculate (assoc setup-args :re-sort? true)))

(defn set-filter
  [{:keys [] :as setup-args} k filter-data]
  (let [lower-bound (:lower-bound-value filter-data)
        upper-bound (:upper-bound-value filter-data)
        custom-filter-fn (get filter/custom-filter-functions k)]
    (if (and
         (or (not custom-filter-fn)
             (nil? filter-data))                                                                    ;; remove custom filter
         (not upper-bound)
         (or (not lower-bound)
             (and (or (coll? lower-bound)
                      (sequential? lower-bound)
                      (list? lower-bound)
                      (string? lower-bound))
                  (empty? lower-bound))))
      (remove-filter setup-args k)
      (add-filter setup-args k filter-data))))

(defn set-sort-by
  [{:keys [view-settings-state ls-path] :as setup-args} k dir]
  (let [old-sort-by (:sort-by @view-settings-state)
        old-sort-dir (:sort-direction @view-settings-state)
        change-secondary? (or (not= k old-sort-by)
                              (= dir old-sort-dir))
        secondary-sort-by (if change-secondary?
                            old-sort-by
                            (:secondary-sort-by @view-settings-state))
        secondary-dir (if change-secondary?
                        old-sort-dir
                        (:secondary-sort-direction @view-settings-state))]
    (when ls-path
      (ls/update-in (ls-path)
                    assoc
                    :sort-by k
                    :sort-direction dir
                    :secondary-sort-by secondary-sort-by
                    :secondary-sort-direction secondary-dir))
    (swap! view-settings-state assoc
           :sort-by k
           :sort-direction dir
           :secondary-sort-by secondary-sort-by
           :secondary-sort-direction secondary-dir)
    (recalculate (assoc setup-args :re-sort? true))))

(defn is-sorted-by
  [view-settings-state k]
  (when (= k (:sort-by @view-settings-state))
    (:sort-direction @view-settings-state)))

(defn is-secondary-sorted-by
  [view-settings-state k]
  (when (= k (:secondary-sort-by @view-settings-state))
    (:secondary-sort-direction @view-settings-state)))

(defn set-universal-search!
  [{:keys [state] :as setup-args} val]
  (swap! state assoc-in [:search :input] (str val))
  (let [search-len (count (str val))
        old-timeout-handle (get-in @state [:search :timeout-handle])
        timeout-handle (when (or (> search-len 2) (= search-len 0))
                         (.setTimeout js/window
                                      (fn [_]
                                        (swap! state dissoc-in [:search :timeout-handle])
                                        (recalculate setup-args))
                                      400))]
    (when old-timeout-handle
      (.clearTimeout js/window old-timeout-handle)
      (swap! state dissoc-in [:search :timeout-handle]))
    (swap! state assoc-in [:search :timeout-handle] timeout-handle)))

(defn set-name-search!
  [{:keys [state] :as setup-args} val]
  (swap! state assoc-in [:search-name :input] (str val))
  (let [search-len (count (str val))
        old-timeout-handle (get-in @state [:search-name :timeout-handle])
        timeout-handle (when (or (> search-len 2) (= search-len 0))
                         (.setTimeout js/window
                                      (fn [_]
                                        (swap! state dissoc-in [:search-name :timeout-handle])
                                        (recalculate setup-args))
                                      400))]
    (when old-timeout-handle
      (.clearTimeout js/window old-timeout-handle)
      (swap! state dissoc-in [:search-name :timeout-handle]))
    (swap! state assoc-in [:search-name :timeout-handle] timeout-handle)))

;;width related fns:

(defn limit-between
  [val min-val max-val]
  (-> val
      (max min-val)
      (min max-val)))

(defn get-saved-width
  [{:keys [column-widths-state]} k]
  (let [saved-width (get @column-widths-state k)
        min-width (-> (widths/get-column-width k)
                      :min-width
                      (or widths/min-width))
        max-width (-> (widths/get-column-width k)
                      :max-width
                      (or widths/max-width))]
    (when saved-width
      (limit-between saved-width min-width max-width))))

(defn get-width
  [{:keys [column-widths-state]} k]
  (let [min-width (-> (widths/get-column-width k)
                      :min-width
                      (or widths/min-width))
        max-width (-> (widths/get-column-width k)
                      :max-width
                      (or widths/max-width))
        default-width (-> (widths/get-column-width k)
                          :default-width
                          (or widths/default-width))]
    (-> @column-widths-state
        k
        (or default-width)
        (limit-between min-width max-width))))

(defn begin-width-update!
  [hover-atom k start-width start-x]
  (swap! app-db/app-state assoc :global-cursor :resize-ew)
  (swap! hover-atom assoc-in
         [:resize-start-width k]
         {:width   start-width
          :start-x start-x}))

(defn end-width-update!
  [hover-atom k]
  (swap! app-db/app-state dissoc :global-cursor)
  (swap! hover-atom p/dissoc-in [:resize-start-width k]))

(defn clear-width!
  [{:keys [column-widths-state]} recompute-grid-sizes! hover-atom k]
  (swap! column-widths-state dissoc k)
  (swap! hover-atom p/dissoc-in [:resize-start-width k])
  (recompute-grid-sizes!))

(defn update-width!
  [{:keys [column-widths-state ls-path] :as setup-args}
   recompute-grid-sizes! hover-atom k current-x]
  (if (nil? current-x)
    (clear-width! setup-args recompute-grid-sizes! hover-atom k)
    (let [orig-width (get-in @hover-atom [:resize-start-width k :width])
          start-x (get-in @hover-atom [:resize-start-width k :start-x])
          tried-width (+ orig-width (- current-x start-x))
          new-val (-> tried-width
                      (max (-> (widths/get-column-width k)
                               :min-width
                               (or widths/min-width)))
                      (min (-> (widths/get-column-width k)
                               :max-width
                               (or widths/max-width))))
          cursor (cond (< tried-width new-val)
                       :resize-not-allowed
                       :else
                       :resize-ew)]
      (swap! column-widths-state assoc k new-val)
      (swap! app-db/app-state assoc :global-cursor cursor)
      (when ls-path
        (ls/update-in (ls-path :column-widths) assoc k new-val)))))

(defn get-styling
  [hover-atom k]
  (when (get-in @hover-atom [:resize-start-width k])
    "resizing"))

(defn get-selected-items
  [{:keys [state]}]
  (let [selected (:selected @state)
        selected-ids (if (= :all selected)
                       (->> @state
                            :sorted-grid-entities
                            (map :db/id))
                       (keys selected))]
    selected-ids))

(defn get-selected-entities
  "The selected rows themselves, not their ids.

   A caller that has to show what was selected cannot turn the ids back into
   entities through the session cache: grid rows are fetched by the grid and
   never land there, so `session/get-entity` returns nil for every one of
   them."
  [{:keys [state]}]
  (let [selected (:selected @state)
        grid-entities (:sorted-grid-entities @state)]
    (->> grid-entities
         (filter (fn [grid-entity]
                   (or (= :all selected)
                       (contains? selected (:db/id grid-entity)))))
         (vec))))

(defn clear-selection!
  [{:keys [state]}]
  (swap! state assoc :selected {}))

(defn select-all-but
  [{:keys [state]} id]
  (swap! state assoc
         :selected
         (->> @state
              :sorted-grid-entities
              (keep (fn [e]
                      (when-not (= (:db/id e) id)
                        [(:db/id e) true])))
              (into {}))))

(defn open-filter-curtain
  [{:keys [options field-key field-schema current-filter pipeline field-label-fn update-fn!]} e]
  (when e
    (dg/prevent-and-stop e))
  (modals/open-modal!
   filter-curtain/modal-key
   {:field-key      field-key
    :field-schema   field-schema
    :field-label-fn field-label-fn
    :data           current-filter
    :pipeline       pipeline
    :options        options                                                                         ;;list of [field-key field-schema] pairs
    :on-ok!         update-fn!}))

(defn groupable-attribute?
  [k]
  (let [tenant-schema @session/tenant-schema
        fs (registry/get-field-schema tenant-schema k)
        data-type (:field/data-type fs)
        value-type (:value-type fs)
        entity-types (:field/entity-types fs)
        groupable-types [:entity.type/named-value
                         :entity.type/stage
                         :entity.type/opportunity-stage
                         :entity.type/sales-opportunity-stage
                         :entity.type/exit-opportunity-stage
                         :entity.type/account
                         :entity.type/fof-asset]]
    (or (some #{data-type} [:data.type/keyword :data.type/keyword-multi])
        (and (= data-type :data.type/ref-single)
             (= value-type :entity)
             (some (set entity-types) groupable-types)))))

(defn clear-focus!
  [state]
  (swap! state dissoc
         :focused-cell-col
         :focused-cell-row
         :focus?
         :error
         :has-focus?))

(defn make-cell-active
  [{:keys [row col state]}]
  (swap! state assoc
         :active-cell-row row
         :active-cell-col col)
  (when-not (and (= (:active-cell-col @state)
                    (:focused-cell-col @state))
                 (= (:active-cell-row @state)
                    (:focused-cell-row @state)))
    (clear-focus! state)))

(defn deactivate-cell!
  [state]
  (swap! state dissoc
         :active-cell-row
         :active-cell-col))

(defn add-focus!
  [{:keys [state active-col active-row]}]
  (let [active-col (if active-col
                     active-col
                     (:active-cell-col @state))
        active-row (if active-row
                     active-row
                     (:active-cell-row @state))]
    (deactivate-cell! state)
    (swap! state assoc
           :focused-cell-col active-col
           :focused-cell-row active-row
           :has-focus? true
           :focus? true)))

(defn move-to-next-cell!
  [{:keys [state col-count row-count]}]
  (cond (not= (:active-cell-col @state) (dec col-count))
        (swap! state update :active-cell-col inc)
        (and (= (:active-cell-col @state) (dec col-count))
             (= (:active-cell-row @state) (dec row-count)))
        (logging/info "Last cell reached")
        :else
        (swap! state assoc
               :active-cell-col 0
               :active-cell-row (inc (:active-cell-row @state))))
  (clear-focus! state))

(defn reset-focus!
  [{:keys [focused-cell-row focused-cell-col state ref-atom]}]
  (make-cell-active {:row   focused-cell-row
                     :col   focused-cell-col
                     :state state})
  (.focus (:grid-ref @ref-atom))
  (clear-focus! state))

(defn handle-cell-key-down
  [{:keys [state row-count col-count ref-atom]} e]
  (when (and (not (:field-editor-opened? @state))
             (not (dg/event-meta-key e)))                                                           ;; when a meta-key is pressed allow browser to do default action - ctrl + c, ctrl + r etc....
    (let [key (dg/event-key-code e)
          active-cell-col (:active-cell-col @state)
          active-cell-row (:active-cell-row @state)
          focused-cell-row (:focused-cell-row @state)
          focused-cell-col (:focused-cell-col @state)]
      (case key
        ; ArrowUp
        38 (when (not= active-cell-row 0)
             (swap! state assoc :active-cell-row (dec active-cell-row))
             (clear-focus! state))
        ; ArrowDown
        40 (when (not= active-cell-row (dec row-count))
             (swap! state assoc :active-cell-row (inc active-cell-row))
             (clear-focus! state))
        ; ArrowLeft
        37 (when (not= active-cell-col 0)
             (swap! state assoc :active-cell-col (dec active-cell-col))
             (clear-focus! state))
        ; ArrowRight
        39 (when (not= active-cell-col (dec col-count))
             (swap! state assoc :active-cell-col (inc active-cell-col))
             (clear-focus! state))
        9 (move-to-next-cell! {:state     state
                               :col-count col-count
                               :row-count row-count})
        ; Space
        32 (add-focus! {:state state})
        ; Enter
        13
        (if (and focused-cell-row focused-cell-col)
          (reset-focus! {:focused-cell-row focused-cell-row
                         :focused-cell-col focused-cell-col
                         :state            state
                         :ref-atom         ref-atom})
          (add-focus! {:state state}))
        ; F2
        113 (add-focus! {:state state})
        ; ESC
        27 (reset-focus! {:focused-cell-row focused-cell-row
                          :focused-cell-col focused-cell-col
                          :state            state
                          :ref-atom         ref-atom})
        (logging/info "On key down state x " key " >> " @state))
      #_(.preventDefault e)
      #_(.stopPropagation e))))

(defn show-tooltip?
  [columns column-key]
  (let [field-schema (:field-schema (get columns column-key))
        grid-type (-> field-schema
                      :field/default-visibility
                      :grid)]
    (editor-types/text-like-editor-type? grid-type)))

(defn ref-field?
  [{:keys [columns col]}]
  (let [data-key (:data-key (get columns col))]
    (vector? data-key)))

(defn read-only-access?
  [{:keys [attribute entity pipeline-id]}]
  (let [entity-type (:entity/type entity)
        write-access? (state-permissions/field-write-access? attribute entity-type pipeline-id)
        ;; hard-coded special case for now - improve when logic gets more complex:
        passed? (= (:oppo/status entity) :oppo.status/passed)
        pass-attribute? (contains? #{:oppo/pass-reason-secondary
                                     :oppo/pass-reason-v2
                                     :oppo/passed-on
                                     :oppo/passed-by}
                                   attribute)]
    (or (and (not passed?)
             pass-attribute?)
        (not write-access?))))

(defn read-only-field?
  [data-key read-only-fields]
  (some #{data-key} read-only-fields))

(defn handle-cell-click!
  [{:keys [columns col row sorted-grid-entities state]}]
  (let [column-key (:data-key (get columns col))
        row-data (nth sorted-grid-entities row)
        cell-value (get row-data column-key)]
    (if (and (cu/blank-value? cell-value)
             (not (ref-field? {:columns columns
                               :col     col})))
      (add-focus! {:state      state
                   :active-col col
                   :active-row row})
      (make-cell-active {:row   row
                         :col   col
                         :state state}))))

;;TODO: consider passing only necessary info instead of the full deref'd grid-state:
(defn default-no-rows-render
  [grid-state-values entity-type {:keys [create-fn]}]
  (r/as-element
   [:div
    {:class "my-6 text-center"}
    (cond (not (:loaded grid-state-values))
          (loc/tr :grid-loading-data)

          ;; there are entities, but all were filtered out:
          (seq (:grid-entities grid-state-values))
          [no-results-view/no-entities-found-view]

          :else
          [no-results-view/no-entities-view
           {:entity-type entity-type
            :grid-state-values grid-state-values
            :create-fn create-fn}])]))

(defn default-row-data-getter
  [sorted-entities selected index]
  (let [data (when (< index (count sorted-entities))
               (nth sorted-entities index))
        selected? (or (= selected :all)
                      (get selected (:db/id data)))]
    (if selected?
      (assoc data :selected true)
      data)))
